/**
 * MarketplaceSettings — privacy + availability controls embedded in /profile.
 *
 * Privacy-first: every toggle defaults to LOCKED DOWN; teachers explicitly opt
 * in to discovery. Hiring users never see anything the teacher hasn't allowed.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/Skeleton";
import {
  Briefcase, Lock, ShieldCheck, Globe, Eye, Loader2,
  Sparkles, MapPin, Banknote, GraduationCap, Save, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { ToggleRow, ModeChip, Field } from "@/components/marketplace/SettingsFields";
import AutofillDraftModal from "@/components/marketplace/AutofillDraftModal";

const VISIBILITY_OPTIONS = [
  { v: "private",          label: "Private",          hint: "Hidden from everyone except you." },
  { v: "recruiters_only",  label: "Recruiters only",  hint: "Discoverable only to verified hiring users." },
  { v: "public",           label: "Public",           hint: "Discoverable to recruiters and signed-in teachers." },
];

const JOB_TYPES = [
  { v: "full_time",        label: "Full-time" },
  { v: "part_time",        label: "Part-time" },
  { v: "freelance",        label: "Freelance" },
  { v: "visiting_faculty", label: "Visiting faculty" },
  { v: "internship",       label: "Internship" },
];

export default function MarketplaceSettings() {
  const [m, setM] = useState(null);
  const [facets, setFacets] = useState(null);
  const [saving, setSaving] = useState(false);
  const [strengthPreview, setStrengthPreview] = useState(0);
  const [autofilling, setAutofilling] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showDraft, setShowDraft] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [me, fac] = await Promise.all([
          api.get("/marketplace/me"),
          api.get("/marketplace/facets"),
        ]);
        setM(me.data);
        setFacets(fac.data);
        setStrengthPreview(me.data.profile_strength || 0);
      } catch {
        // silent
      }
    })();
  }, []);

  if (!m || !facets) {
    return <Skeleton className="h-72 rounded-3xl" data-testid="marketplace-skeleton" />;
  }

  const update = (patch) => setM((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/marketplace/me", m);
      setM(data);
      setStrengthPreview(data.profile_strength || 0);
      toast.success("Marketplace settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await api.post("/marketplace/autofill", {});
      setDraft(data);
      setShowDraft(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        toast.error("Add a resume or marketplace profile first.", {
          description: "Generate a TeeBee CV — we'll fill the rest automatically.",
        });
      } else {
        toast.error(err?.response?.data?.detail || "Could not auto-fill");
      }
    } finally {
      setAutofilling(false);
    }
  };

  const applyDraft = async () => {
    if (!draft) return;
    try {
      const allowed = ["headline", "teaching_categories", "subjects", "grade_levels", "experience_years", "languages_taught"];
      const payload = {};
      allowed.forEach((k) => { if (draft[k] !== undefined && draft[k] !== null) payload[k] = draft[k]; });
      await api.post("/marketplace/autofill/apply", payload);
      const { data: refreshed } = await api.get("/marketplace/me");
      setM(refreshed);
      setStrengthPreview(refreshed.profile_strength || 0);
      setShowDraft(false);
      toast.success("Marketplace profile updated from CV");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed");
    }
  };

  const toggleCategory = (cat) => {
    const has = (m.teaching_categories || []).includes(cat);
    update({
      teaching_categories: has
        ? m.teaching_categories.filter((c) => c !== cat)
        : [...(m.teaching_categories || []), cat],
    });
  };

  return (
    <div className="tb-card-feature relative overflow-hidden" data-testid="marketplace-settings">
      <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
      <div className="relative space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div>
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Briefcase className="h-3 w-3" /> TeeBee Educator Marketplace
            </span>
            <h2 className="mt-3 font-display text-xl sm:text-2xl tracking-tight font-medium">
              Be discoverable on your terms
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
              Recruiters and institutions can find you — but only the fields you allow are ever shown.
              Default privacy is LOCKED. Flip the toggles you&apos;re comfortable with.
            </p>
            <div className="mt-3">
              <Button onClick={autofill} variant="outline" className="rounded-full" disabled={autofilling} data-testid="mp-autofill">
                {autofilling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1.5 text-amber-500" />}
                Auto-fill from my CV
              </Button>
              <span className="ml-2 text-[11px] text-muted-foreground">Uses TeeBee Intelligence — review before saving.</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Profile strength</p>
            <p className="font-display text-3xl tracking-tight font-medium">{strengthPreview}<span className="text-base text-muted-foreground">/100</span></p>
            <div className="mt-1 h-1.5 w-32 rounded-full bg-muted overflow-hidden ml-auto">
              <div className="h-full bg-primary transition-all" style={{ width: `${strengthPreview}%` }} />
            </div>
          </div>
        </div>

        {/* Master switch */}
        <div className="rounded-2xl border border-border p-4 flex items-start gap-3 bg-card/50">
          <span className="h-9 w-9 rounded-xl tb-chip-mint grid place-items-center">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Available for hiring</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              When ON, your masked card appears in the Discover Educators search to recruiters.
            </p>
          </div>
          <Switch
            checked={!!m.available_for_hiring}
            onCheckedChange={(v) => update({ available_for_hiring: v })}
            data-testid="mp-toggle-available"
          />
        </div>

        {/* Visibility radio */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Who can see your profile
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {VISIBILITY_OPTIONS.map((o) => {
              const active = m.profile_visibility === o.v;
              return (
                <button
                  key={o.v}
                  onClick={() => update({ profile_visibility: o.v })}
                  className={`text-left rounded-2xl border p-3 transition-all ${
                    active ? "border-primary/60 bg-primary/5" : "border-border hover:border-primary/30"
                  }`}
                  data-testid={`mp-visibility-${o.v}`}
                >
                  <p className="text-sm font-medium inline-flex items-center gap-2">
                    {o.v === "private" && <Lock className="h-3.5 w-3.5" />}
                    {o.v === "recruiters_only" && <ShieldCheck className="h-3.5 w-3.5" />}
                    {o.v === "public" && <Globe className="h-3.5 w-3.5" />}
                    {o.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{o.hint}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Granular switches */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <ToggleRow icon={Eye}        label="Publish my CV"           hint="Lets recruiters request CV access."
                     val={m.publish_cv}            onChange={(v) => update({ publish_cv: v })}            testid="mp-toggle-cv" />
          <ToggleRow icon={Eye}        label="Allow CV download"        hint="Otherwise CV is preview-only."
                     val={m.allow_cv_download}     onChange={(v) => update({ allow_cv_download: v })}     testid="mp-toggle-cv-dl" />
          <ToggleRow icon={Globe}      label="Allow direct contact"     hint="Recruiters can message you directly."
                     val={m.allow_direct_contact}  onChange={(v) => update({ allow_direct_contact: v })}  testid="mp-toggle-contact" />
          <ToggleRow icon={Lock}       label="Hide phone number"         hint="Phone stays hidden until you approve."
                     val={m.hide_phone}            onChange={(v) => update({ hide_phone: v })}            testid="mp-toggle-phone" />
          <ToggleRow icon={Lock}       label="Hide email"                hint="Email stays hidden until you approve."
                     val={m.hide_email}            onChange={(v) => update({ hide_email: v })}            testid="mp-toggle-email" />
          <ToggleRow icon={ShieldCheck} label="Route through TeeBee only" hint="All recruiter outreach uses our request flow."
                     val={m.use_teebee_contact_only} onChange={(v) => update({ use_teebee_contact_only: v })} testid="mp-toggle-route" />
          <ToggleRow icon={Banknote}   label="Show salary expectations"  hint="Only the band, never an exact number."
                     val={m.show_salary}           onChange={(v) => update({ show_salary: v })}           testid="mp-toggle-salary" />
          <ToggleRow icon={Globe}      label="Open to global opportunities"
                     val={m.open_to_global}        onChange={(v) => update({ open_to_global: v })}        testid="mp-toggle-global" />
        </div>

        {/* Teaching mode */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Teaching mode</p>
          <div className="grid grid-cols-3 gap-2.5">
            <ModeChip label="Online"  val={m.open_to_online}  onChange={(v) => update({ open_to_online: v })}  testid="mp-mode-online" />
            <ModeChip label="Offline" val={m.open_to_offline} onChange={(v) => update({ open_to_offline: v })} testid="mp-mode-offline" />
            <ModeChip label="Hybrid"  val={m.open_to_hybrid}  onChange={(v) => update({ open_to_hybrid: v })}  testid="mp-mode-hybrid" />
          </div>
        </div>

        {/* Categories */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2 inline-flex items-center gap-2">
            <GraduationCap className="h-3 w-3" /> Categories you teach
          </p>
          <p className="text-[11px] text-muted-foreground mb-2">Curricular</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {facets.curricular_categories.map((c) => {
              const active = (m.teaching_categories || []).includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCategory(c)}
                  className={`rounded-full text-[11px] px-2.5 py-1 border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}
                  data-testid={`mp-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">Extra-curricular</p>
          <div className="flex flex-wrap gap-1.5">
            {facets.extra_curricular_categories.map((c) => {
              const active = (m.teaching_categories || []).includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCategory(c)}
                  className={`rounded-full text-[11px] px-2.5 py-1 border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}
                  data-testid={`mp-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick text fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Headline" testid="mp-headline">
            <input
              type="text"
              className="tb-input"
              placeholder="e.g. CBSE Maths · Grades 9–12 · 8 yrs"
              value={m.headline || ""}
              onChange={(e) => update({ headline: e.target.value })}
            />
          </Field>
          <Field label="Years of experience" testid="mp-exp">
            <input
              type="number"
              min={0}
              className="tb-input"
              value={m.experience_years || ""}
              onChange={(e) => update({ experience_years: e.target.value ? parseInt(e.target.value, 10) : null })}
            />
          </Field>
          <Field label="Preferred location" icon={MapPin} testid="mp-location">
            <input
              type="text"
              className="tb-input"
              placeholder="e.g. Bengaluru, IN"
              value={m.preferred_location || ""}
              onChange={(e) => update({ preferred_location: e.target.value })}
            />
          </Field>
          <Field label="Demo class URL" testid="mp-demo">
            <input
              type="url"
              className="tb-input"
              placeholder="https://"
              value={m.demo_class_url || ""}
              onChange={(e) => update({ demo_class_url: e.target.value })}
            />
          </Field>
          <Field label="Expected monthly salary (min) — INR" icon={Banknote} testid="mp-salary-min">
            <input
              type="number"
              min={0}
              className="tb-input"
              value={m.expected_salary_min || ""}
              onChange={(e) => update({ expected_salary_min: e.target.value ? parseInt(e.target.value, 10) : null })}
            />
          </Field>
          <Field label="Expected monthly salary (max) — INR" icon={Banknote} testid="mp-salary-max">
            <input
              type="number"
              min={0}
              className="tb-input"
              value={m.expected_salary_max || ""}
              onChange={(e) => update({ expected_salary_max: e.target.value ? parseInt(e.target.value, 10) : null })}
            />
          </Field>
        </div>

        {/* Job-type chips */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-2">Preferred job types</p>
          <div className="flex flex-wrap gap-1.5">
            {JOB_TYPES.map((jt) => {
              const active = (m.preferred_job_types || []).includes(jt.v);
              return (
                <button
                  key={jt.v}
                  onClick={() => update({
                    preferred_job_types: active
                      ? (m.preferred_job_types || []).filter((t) => t !== jt.v)
                      : [...(m.preferred_job_types || []), jt.v],
                  })}
                  className={`rounded-full text-[11px] px-2.5 py-1 border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}
                  data-testid={`mp-jobtype-${jt.v}`}
                >
                  {jt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Save */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <p className="text-[11px] text-muted-foreground">
            Privacy by design — recruiters only see fields you explicitly allow.
          </p>
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="mp-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save marketplace settings
          </Button>
        </div>
      </div>

      {/* Auto-fill draft review modal */}
      {showDraft && (
        <AutofillDraftModal
          draft={draft}
          setDraft={setDraft}
          onApply={applyDraft}
          onClose={() => setShowDraft(false)}
        />
      )}
    </div>
  );
}
